import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Row, OverlayTrigger, Tooltip, Container } from "react-bootstrap";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";
import { LLMVisitFeedback } from "../../../../amplify/appsync/query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faCheckCircle, faExclamationTriangle, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { getLocalDateTimeStr } from "../../../../utils/time";
import PersonalIdentifier from "../../../common/PersonalIdentifier";

export const AnatomyFindingSection = (props: {
    patientVisit?: PatientVisit,
    finding: any,
    index: number,
    expandedKey: string,
    expandedFinding: string,
    feedbacks: LLMVisitFeedback[],
    setFeedbacks: any,
    scanMulti: any,
}) => {

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [showFullImaging, setShowFullImaging] = useState<boolean>(false);

    useEffect(() => {
        if (props.expandedFinding === props.expandedKey) {
            setIsOpen(true);
        }
    }, [props.expandedFinding, props.expandedKey]);

    const getCategoryColor = (category: any) => {
        const colorMap: any = {
            'Acute traumatic injury': '#ff6b6b',
            'Chronic disease': '#ffa94d',
            'Post-surgical or treatment-related changes': '#74c0fc',
            'Procedural or medical device issue': '#a9e34b',
            'No significant findings': '#69db7c',
            'Metastatic disease': '#cc5de8',
            'Significant incidental finding': '#ff922b',
            'Acute non-traumatic condition': '#4dabf7',
            'Infectious process': '#e599f7',
            'Indeterminate finding': '#868e96',
            'Developmental or congenital anomaly': '#3bc9db'
        };

        return colorMap[category] || '#ced4da';
    };

    // Find corresponding imaging study
    const matchingStudy = props.scanMulti?.imaging?.find((study: any) => {
        const studyTime = new Date(study.result_time).getTime();
        const findingTime = new Date(props.finding.result_time).getTime();
        return study.study === props.finding.study && studyTime === findingTime;
    });

    const normalize = (str: string) => str.replace(/\s+/g, " ").trim(); // replaces all whitespace (including newlines, tabs) with single spaces

    const quoteExists = matchingStudy?.impression
        ? normalize(matchingStudy.impression).includes(normalize(props.finding.quote))
        : false;

    // Highlight quote in the full imaging impression
    const getHighlightedImpression = () => {
        if (!matchingStudy) return 'N/A';
        if (!props.finding.quote) return matchingStudy.impression;

        const regex = new RegExp(`(${props.finding.quote})`, 'gi');
        return matchingStudy.impression.replace(regex, '<mark>$1</mark>');
    };

    return (
        <Card
            key={`finding-${props.finding.id}`}
            className="shadow-sm"
            style={{
                borderLeft: `4px solid ${getCategoryColor(props.finding.category)}`,
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px'
            }}
        >
            {/* Finding Header */}
            <Card.Header
                className="d-flex justify-content-between align-items-center bg-white"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <Container>
                    <Row>
                        <Col md={10}>
                            <div className="fw-medium">
                                <span className="d-inline-block bg-light rounded-circle text-center me-2" style={{ width: '24px', height: '24px', lineHeight: '24px', fontSize: '0.875rem' }}>
                                    {props.index + 1}
                                </span>
                                {props.finding.finding_text} ({props.finding.days_ago} days ago)

                                {/* Quote Validation Icon */}
                                {quoteExists ? (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Quote found in imaging study</Tooltip>}>
                                        <span style={{ cursor: 'pointer' }}>
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
                                        </span>
                                    </OverlayTrigger>
                                ) : (
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Quote NOT found in imaging study</Tooltip>}>
                                        <span style={{ cursor: 'pointer' }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning ms-2" />
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </div>
                            <div className="small text-muted ms-4">{props.finding.region}</div>
                        </Col>

                        <Col md={2} className="text-right">
                            <LLMDashboardFeedbackBar patientVisit={props.patientVisit} feedbackTarget={`anatomy/${props.finding.finding_text.substring(0, 20) || "N/A"}/${props.finding.result_time || "N/A"}`} feedbacks={props.feedbacks} setFeedbacks={props.setFeedbacks} />
                            <Button
                                variant="primary"
                                className="dashboard-round-btn me-2"
                                onClick={(e: any) => {e.stopPropagation(); setIsOpen(!isOpen)}}
                            >
                                <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </Card.Header>

            {/* Expanded Details */}
            {isOpen && (
                <Card.Body className="bg-light">
                    <Row className="g-3">
                        <Col xs={4}>
                            <div className="text-muted small">Study</div>
                            <div>{props.finding.study || "N/A"}</div>
                        </Col>
                        <Col xs={4}>
                            <div className="text-muted small">Date</div>
                            <div><PersonalIdentifier identifier={getLocalDateTimeStr(props.finding.result_time) || "N/A"} /></div>
                        </Col>
                        <Col xs={4}>
                            <div className="text-muted small">Category</div>
                            <Badge
                                pill
                                style={{
                                    backgroundColor: getCategoryColor(props.finding.category),
                                    color: '#fff'
                                }}
                            >
                                {props.finding.category}
                            </Badge>
                        </Col>
                        <Col xs={12}>
                            <div className="text-muted small">
                                Full Description
                            </div>
                            <div className="fst-italic mt-1 border-start ps-2 border-secondary">
                                "{props.finding.quote || 'No description available'}"
                            </div>
                        </Col>

                        {/* Full Imaging Study Toggle */}
                        {matchingStudy && (
                            <Col xs={12}>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setShowFullImaging(!showFullImaging)}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                    {showFullImaging ? "Hide Full Imaging Study" : "Show Full Imaging Study"}
                                </Button>

                                {showFullImaging && (
                                    <div className="mt-3 border p-2 bg-white" style={{ whiteSpace: 'pre-wrap' }}
                                        dangerouslySetInnerHTML={{ __html: getHighlightedImpression() }}
                                    />
                                )}
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            )}
        </Card>
    );
};
